import { Link, usePage } from "@inertiajs/react"
import { SeoHead } from "@/components/SeoHead"
import { organizationNode } from "@/lib/seo"
import type { PageProps } from "@/types/inertia"
import { MarketingLayout } from "@/components/marketing/MarketingLayout"
import { Section, Eyebrow } from "@/components/marketing/sections/Section"
import { track } from "@/lib/analytics"

// Unlisted work-samples page at /work, sent by hand to prospects who ask for a
// sample. noindex, and absent from sitemap/llms.txt/nav/footer.
//
// Deliberately NOT added to robots.txt — same reasoning as /pricing: that file
// is world-readable, so a Disallow line advertises the URL, and blocking the
// crawl stops crawlers ever reading the noindex tag below.
//
// The page is organised by ENGAGEMENT TYPE, not by client. A prospect's real
// question is "what does working with you look like for a show like mine", and
// the five blocks answer five different versions of that: ongoing production,
// launch from zero, mid-run takeover, and two shows Harry hosts himself.
//
// Download totals appear ONLY for shows FullCast currently produces or Harry
// owns. The two former clients kept their feeds and kept publishing, so their
// current numbers measure someone else's production, not ours.
const DESCRIPTION =
  "Five podcasts across five kinds of engagement — ongoing production, a launch from scratch, a mid-run takeover, and two shows Harry Duran hosts himself."

// Embeds use youtube-nocookie and loading="lazy": ten players is a lot of
// weight, and most are below the fold on every viewport. Every ID verified
// public and embeddable via the YouTube Data API on 2026-08-27.
type Show = {
  key: string
  kind: string
  name: string
  alsoKnownAs?: string
  host: string
  role: string
  period: string
  url?: string
  urlLabel?: string
  episodeId: string
  episodeTitle: string
  shortId: string
  shortTitle: string
  description: string
  did: string
  why: string
}

const CLIENT_WORK: Show[] = [
  {
    key: "mhmw",
    kind: "Ongoing weekly production",
    name: "Mental Health in a Modern World",
    host: "Greg Schmaus",
    role: "Full production, via FullCast",
    period: "Weekly since February 2026 · 25 episodes",
    url: "https://healing4d.com/podcasts",
    urlLabel: "healing4d.com/podcasts",
    episodeId: "TF0ZTgSoZVA",
    episodeTitle: "Truth About Psychedelics, Soul Retrieval & Healing",
    shortId: "zf_lMDplwfc",
    shortTitle: "Fate or Free Will: Which Do You Choose?",
    description:
      "A weekly long-form show hosted by holistic mental health coach Greg Schmaus, on anxiety, depression, trauma, and the distance between conventional mental health care and whole-person treatment. Episodes run past an hour.",
    did: "My studio produces and edits both the audio and the video, publishes to his YouTube channel and his website, and repurposes each episode into a written post. I own the relationship, the workflow design and the standard; producers and editors on my team execute the recurring post-production under handoffs and review standards I wrote.",
    why: "This is the closest thing to a typical engagement. An expert-led practice whose principal had no channel, now publishing every Thursday without a gap and without production eating his week — from 6 downloads in its opening month to 282 in its seventh.",
  },
  {
    key: "imyt",
    kind: "Developed and launched from scratch",
    name: "I'll Meet You There",
    host: "Paige Nolan",
    role: "Development, launch and opening run, via FullCast",
    period: "Launched October 2023 · now in its third season, produced in-house",
    episodeId: "TdUFkOHM0jk",
    episodeTitle: "Beth LeManach — Cooking and Entertaining As An Act of Love",
    shortId: "-gcjfECjcGw",
    shortTitle: "Inspiration for a New Generation",
    description:
      "A conversation show with writer and coach Paige Nolan about the interior life — the questions people carry and rarely say out loud. The episode below is from the opening season, which we produced.",
    did: "Developed and launched the show, then produced the first handful of episodes. Paige has since taken it back in-house with a producer of her own, and it is on its third season.",
    why: "This is the launch case: a host with something to say and no show yet. The handoff is the honest part of it. A launch has done its job when the show can run without the people who started it, and this one has been running on its own for two years.",
  },
  {
    key: "qbc",
    kind: "Took over an existing show mid-run",
    name: "Quantum Biology Collective",
    alsoKnownAs: "now rebranded as The Reverse Aging Club",
    host: "Meredith Oke",
    role: "Production takeover, via FullCast",
    period: "Show running since 2022 · past 190 episodes · now produced in-house",
    episodeId: "9ZFYhDkjec0",
    episodeTitle: "How Beehives Reveal Hidden Human Secrets!",
    shortId: "DFa0OjAa_Tw",
    shortTitle: "Breaking the Burnout Myth — It's Not What You Think",
    description:
      "A long-running show on quantum biology, circadian health and light, hosted by Meredith Oke. It has since been rebranded as The Reverse Aging Club, which is the channel these two clips play from.",
    did: "Took over production of a show already well into its run, raised the production standard and the consistency, and handed it back in-house when Meredith was ready to bring it in.",
    why: "This is the takeover case, and it is most of what comes to us. The show already exists, already has a backlog, an audience and a cadence, and the only real question is whether someone can step into the middle of it without breaking what is working.",
  },
]

const OWN_SHOWS: Show[] = [
  {
    key: "vertical-farming",
    kind: "Hosted and produced end to end",
    name: "Vertical Farming Podcast",
    host: "Harry Duran",
    role: "Host and Executive Producer",
    period: "2020–present · 186 episodes · 171,004 downloads",
    url: "https://igrownews.com/podcast/vertical-farming",
    urlLabel: "igrownews.com/podcast/vertical-farming",
    episodeId: "bGQrI9nYcw8",
    episodeTitle: "The Humidity Mistake Destroying Your Grow Room Right Now",
    shortId: "DIy347aUe-g",
    shortTitle: "The Harsh Reality of Container Farming",
    description:
      "A long-form interview show on controlled-environment agriculture, food security, and the capital reshaping how food gets grown. Guests are CEOs, founders, researchers and investors across North America, Europe and the Gulf. It started with no audience and became the leading title in its category.",
    did: "Everything, and still do. Research and book every guest, write the outreach, schedule across time zones, prepare the research brief and the question arc, host, and direct the edit. On the commercial side, sold and served more than $120,000 in sponsorship — including a three-season, $40,000 partnership — and produced the host-read spots, branded assets and reporting that came with it.",
    why: "This is the whole job done for a demanding, technical beat, sustained over five years. If your field has its own vocabulary and your audience can tell when a host hasn't done the reading, this is the evidence that we can.",
  },
  {
    key: "podcast-junkies",
    kind: "Twelve years of the same show",
    name: "Podcast Junkies",
    host: "Harry Duran",
    role: "Creator, Host and Executive Producer",
    period: "Since 2014 · 389 episodes · 94,704 downloads",
    url: "https://podcastjunkies.com",
    urlLabel: "podcastjunkies.com",
    episodeId: "h64RhlrBm5A",
    episodeTitle: "Turning Cancer Survival into Inspiring Stories",
    shortId: "P2rNDHTI5qo",
    shortTitle: "Why Are Podcasts Still So Hard to Discover",
    description:
      "A conversation show with podcasters, founders and creatives about the craft and the people behind it. Apple has featured it as a recommended show. It is where I learned to interview.",
    did: "Created the show and have produced it for twelve years, mostly single-handed. Book and research every guest, host and record, then cut the raw conversation down to something that moves. Write the show notes and the titles, handle the audio edit and mix, and publish.",
    why: "The show's reputation is that guests say things they haven't said elsewhere. That is a production decision as much as an interviewing one: leave the pauses that are people thinking, and cut the ones that are people stalling. It is the judgment we bring to someone else's show.",
  },
]

const ALL = [...CLIENT_WORK, ...OWN_SHOWS]

function Embed({
  id,
  title,
  vertical = false,
}: {
  id: string
  title: string
  vertical?: boolean
}) {
  return (
    // The Short's 9:16 frame is inherently taller than the 16:9 episode next to
    // it. 200px wide keeps the two columns close enough in height that the
    // episode column doesn't sit above a slab of dead space.
    <div
      className={
        vertical
          ? "mx-auto aspect-[9/16] w-full max-w-[200px] overflow-hidden rounded-xl border border-hairline bg-black"
          : "aspect-video w-full overflow-hidden rounded-xl border border-hairline bg-black"
      }
    >
      <iframe
        src={`https://www.youtube-nocookie.com/embed/${id}`}
        title={title}
        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
        loading="lazy"
        className="block h-full w-full border-0"
      />
    </div>
  )
}

function ShowBlock({ show, index }: { show: Show; index: number }) {
  return (
    <div id={show.key} className="border-t border-hairline pt-12 first:border-t-0 first:pt-0">
      <div className="text-xs font-semibold uppercase tracking-[0.14em] text-accent-display">
        {String(index).padStart(2, "0")} · {show.kind}
      </div>
      <h3 className="display-2 mt-2">{show.name}</h3>
      {show.alsoKnownAs && <p className="mt-1 text-ink-muted">{show.alsoKnownAs}</p>}
      <p className="mt-3 text-ink-display">
        {show.host} · {show.role}
      </p>
      <p className="mt-1 text-ink-muted">
        {show.url && (
          <>
            <a href={show.url} target="_blank" rel="noreferrer">
              {show.urlLabel}
            </a>
            {" · "}
          </>
        )}
        {show.period}
      </p>

      <div className="mt-8 grid gap-5 sm:grid-cols-[minmax(0,1fr)_200px]">
        <div>
          <Embed id={show.episodeId} title={`${show.name} — full episode`} />
          <p className="mt-2 text-xs font-semibold uppercase tracking-[0.1em] text-ink-muted">
            Full episode
          </p>
          <p className="mt-1 text-sm text-ink-muted">{show.episodeTitle}</p>
        </div>
        <div>
          <Embed id={show.shortId} title={`${show.name} — short`} vertical />
          <p className="mt-2 text-xs font-semibold uppercase tracking-[0.1em] text-ink-muted">
            Short
          </p>
          <p className="mt-1 text-sm text-ink-muted">{show.shortTitle}</p>
        </div>
      </div>

      <p className="mt-7">{show.description}</p>
      <p className="mt-4">
        <strong>What we did.</strong> {show.did}
      </p>
      <p className="mt-4">
        <strong>Why it is relevant.</strong> {show.why}
      </p>
    </div>
  )
}

export default function Work() {
  const base = (usePage<PageProps>().props.app_url || "https://fullcast.co").replace(/\/$/, "")

  return (
    <MarketingLayout>
      <SeoHead
        title="Work samples"
        description={DESCRIPTION}
        noindex
        jsonLd={[organizationNode(base)]}
      />

      <Section containerClassName="max-w-3xl">
        <Eyebrow>FullCast · Work samples</Eyebrow>
        <h1 className="display-1 mt-4">Five shows, and five different jobs</h1>
        <p className="mt-5 text-lg text-ink-body">
          Rather than a reel of clips, here are five podcasts you can watch in
          full, each with a short cut from the same channel, and a plain account
          of what the work actually was. They are grouped by the kind of
          engagement rather than by client, because the useful question is
          usually not "who have you worked with" but "what would you be doing
          for a show like mine".
        </p>

        {/* A skimmable index. Five embed blocks is a long scroll, and the range
            is the point — someone should be able to see it without scrolling. */}
        <ul className="mt-8 border-t border-hairline pt-6">
          {ALL.map((show, i) => (
            <li key={show.key} className="border-b border-hairline py-3">
              <a href={`#${show.key}`} className="no-underline">
                <span className="text-xs font-semibold uppercase tracking-[0.14em] text-accent-display">
                  {String(i + 1).padStart(2, "0")}
                </span>{" "}
                <strong className="text-ink-display">{show.name}</strong>{" "}
                <span className="text-ink-muted">— {show.kind}</span>
              </a>
            </li>
          ))}
        </ul>
      </Section>

      <Section className="border-t border-hairline bg-surface" containerClassName="max-w-3xl">
        <Eyebrow>Client work</Eyebrow>
        <h2 className="display-2 mt-3">Three shows, three ways in</h2>
        <p className="mt-4 text-ink-body">
          One we produce every week right now, one we built from nothing, and
          one we picked up in the middle of its run. Two of the three hosts have
          since brought production in-house, which we have said plainly rather
          than left out.
        </p>
        <div className="mt-14 space-y-14">
          {CLIENT_WORK.map((show, i) => (
            <ShowBlock key={show.key} show={show} index={i + 1} />
          ))}
        </div>
      </Section>

      <Section className="border-t border-hairline" containerClassName="max-w-3xl">
        <Eyebrow>My own shows</Eyebrow>
        <h2 className="display-2 mt-3">Where the standard comes from</h2>
        <p className="mt-4 text-ink-body">
          I host and produce these two myself, which is why I can show you
          everything about them rather than the parts a client has cleared. They
          are also where most of what we do for other people was worked out.
        </p>
        <div className="mt-14 space-y-14">
          {OWN_SHOWS.map((show, i) => (
            <ShowBlock key={show.key} show={show} index={CLIENT_WORK.length + i + 1} />
          ))}
        </div>
      </Section>

      {/* The honest note. Two things a prospect will notice on their own, said
          first: the view counts under the embeds are far smaller than the
          download numbers, and none of these is a breakout hit. */}
      <Section id="the-numbers" className="border-t border-hairline bg-surface" containerClassName="max-w-3xl">
        <div className="rounded-r-xl border-l-[3px] border-accent bg-page px-6 py-6">
          <p>
            <strong>A note on the numbers.</strong> These shows are audio-first
            and predate their YouTube channels, so the view counts under the
            players are much smaller than the download totals. The downloads are
            where the audience actually is. Where a show is no longer ours, we
            have left the download figure off entirely — those numbers now
            measure someone else's production, not ours.
          </p>
          <p className="mt-3">
            None of these is a million-download show. If what you want is a
            producer who can promise a large audience quickly, we are not it,
            and honestly nobody is. What these five demonstrate is the thing we
            do stand behind: a show that keeps going, at a standard that holds,
            without the production eating the host's week. Cadence is the part
            that compounds, and it is the part we take off your desk.
          </p>
        </div>

        <div className="mt-10">
          <Link
            href="/apply"
            onClick={() => track("CTA Click")}
            className="inline-flex items-center rounded-lg bg-accent px-5 py-3 font-medium text-page no-underline"
          >
            Book a fit call
          </Link>
        </div>
      </Section>
    </MarketingLayout>
  )
}
